import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Icons as GridIcons } from "Components/AppGrid/shipeezi-new-grid";
import { useTranslation } from "react-i18next";
import { Badge } from "reactstrap";

import Button, { Color, Colors } from "../../../../../../../components/Form/Button";
import ConfirmChangeStatus from "../../../../../../../components/Generic/ConfirmChangeStatusModal";
import { NotificationToast } from "../../../../../../../components/Generic/Toast/NotificationToast";
import { useAppState } from "../../../../../../../context/StateContext";
import { JobMode } from "../../../../../../../models/job.mode";
import { Row } from "../../../../../../../models/shipeezi.table";
import { Status } from "../../../../../../../util/enums";
import { onSaveItemSelected } from "../../../../../../../util/onSaveItemSelected";
import ModeTypeModal from "./ModeTypeModal";
import useStyles from "./styles";
import useActions from "./useActions";
import { isShowAction } from "Util/permissions";

type Props = {
  permission?: {
    edit: boolean,
    create: boolean,
  }
}

const ModeType = ({ permission }: Props) => {
  const classes = useStyles();
  const { currentUser } = useAppState();
  const { t: translate } = useTranslation();

  const {
    modeTypes,
    total,
    page,
    pageSize,
    hasErrors,
    isLoading,
    setPageSize,
    setHasErrors,
    setCurrentPage,
    handleSearch,
    handleFilter,
    handleSort,
    createMode,
    updateMode,
  } = useActions();

  const [openModal, setOpenModal] = useState<boolean>(false);
  const [selectedModeType, setSelectedModeType] = useState<JobMode>();
  const [rows, setRows] = useState<Row[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState<boolean>(false);
  const [clearSelection, setClearSelection] = useState<boolean>(false);

  const badgeStatus = (status: string) => (
    <Badge
      className={`
        badgeComponent
        ${classes.statusBadge}
        ${status === Status.ACTIVE ? classes.statusBadgeActive : ""}
      `}
    >
      {status}
    </Badge>
  );

  const editModeType = (index: number) => {
    if (hasErrors !== undefined) setHasErrors(undefined);
    const modeToUpdate = modeTypes.find((mode) => mode.id === index);

    if (modeToUpdate) {
      setSelectedModeType(modeToUpdate);
      setOpenModal(true);
    }
  };

  const changeStatusModeType = (index: number) => {
    const modeToUpdate = modeTypes.find((mode) => mode.id === index);

    if (modeToUpdate) {
      updateMode({
        id: Number(index),
        mode: {
          ...modeToUpdate,
          status: modeToUpdate.status === Status.ACTIVE ? Status.INACTIVE : Status.ACTIVE,
        },
      });
    }
  };

  const renderButtonToChangeStatus = () => {
    if (rows.length > 0) {
      return (
        <Button
          tooltip={translate("changeStatus")}
          className={classes.changeStatusButton}
          customColor={Color.INFO}
          onClick={() => setOpenConfirmation(true)}
          data-cy="changeStatus"
        >
          <GridIcons.status color={Colors.INFO.text.color} size={16} className="mr-2" />
          {translate("changeStatus")}
        </Button>
      );
    }
  };

  const handleSaveModeTypeSelected = (rowsArr: number[]) => {
    const rowsSelected = onSaveItemSelected(rowsArr, rows, modeTypes);

    setRows([...rowsSelected]);
  };

  useEffect(() => {
    if (hasErrors !== undefined && !hasErrors) {
      NotificationToast.success(translate("operationPerformedSuccessfully"), {
        id: "request.notification.success.mode.type",
      });
      setHasErrors(undefined);
      setClearSelection(false);
      setRows([]);
    }
  }, [hasErrors]);

  return (
    <div data-cy="modelTypeList">
      <ShipeeziNewGrid
        data={modeTypes}
        columns={[
          { name: "code", title: translate("code") },
          { name: "name", title: translate("name") },
          {
            name: "status",
            title: translate("status"),
            formatComponent: (status: string) => badgeStatus(status),
            hideSearch: true,
          },
        ]}
        totalItems={total}
        loadingData={isLoading}
        emptyData={translate("noInformationToBeShown")}
        pageSize={pageSize}
        pageSizeList={[20, 40, 60, 100]}
        currentPage={page}
        onChangePageSize={setPageSize}
        onNextPage={setCurrentPage}
        onPreviousPage={setCurrentPage}
        onGoToPage={setCurrentPage}
        rowActions={{
          orderBy: ["handleEdit", "handleChange"],
          handleEdit: permission && isShowAction(currentUser, permission.edit)
            ? (modeTypeId) => editModeType(Number(modeTypeId))
            : undefined,
          handleChange: (modeTypeId) => changeStatusModeType(Number(modeTypeId)),
        }}
        handleSelect={(rows: number[]) => handleSaveModeTypeSelected(rows)}
        clearSelection={clearSelection}
        positionActionsColumn="right"
        onSorting={handleSort}
        generalActions={{
          orderBy: {
            left: ["handleSearch", "handleFilterBy"],
            right: ["handleChangeStatus", "handleNewRecord"],
          },
          handleNewRecord: permission && isShowAction(currentUser, permission.create)
            ? {
              text: translate("create"),
              action: () => {
                if (hasErrors !== undefined) setHasErrors(undefined);
                setSelectedModeType(undefined);
                setOpenModal(true);
              },
              tooltip: translate("create"),
            }
            : undefined,
          handleSearch: {
            onSearch: handleSearch,
          },
          handleFilterBy: {
            categories: [
              { name: translate("code"), column: "code" },
              { name: translate("name"), column: "name" },
              {
                name: translate("status"),
                column: "status",
                options: [Status.ACTIVE, Status.INACTIVE],
              },
            ],
            onChange: handleFilter,
          },
          handleChangeStatus: renderButtonToChangeStatus(),
        }}
      />
      {openModal && (
        <ModeTypeModal
          modeToUpdate={selectedModeType}
          open={openModal}
          isLoading={isLoading}
          hasError={hasErrors}
          closeModal={() => setOpenModal(false)}
          submitModeType={(mode: JobMode) => {
            if (selectedModeType && selectedModeType?.id)
              updateMode({
                id: selectedModeType.id,
                mode: { ...selectedModeType, ...mode },
              });
            else createMode({ ...mode, companyId: currentUser?.companyId });
          }}
        />
      )}

      {openConfirmation && (
        <ConfirmChangeStatus
          open={openConfirmation}
          rows={rows}
          isLoading={isLoading}
          hasErrors={hasErrors}
          closeModal={() => setOpenConfirmation(false)}
          update={(rowId, status) => {
            if (rowId) {
              updateMode({ id: rowId, mode: { status } as JobMode });
              setClearSelection(true);
            }
          }}
        />
      )}
    </div>
  );
};

export default ModeType;
